import type { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { renderSymbolPickerSearchRow, SEARCH_MATCH_LIMIT } from "../../../../../shared/utility/symbol-picker-search";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaLoader from "../../../../ui-kit/kuika-loader/kuika-loader";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { KuikaTag } from "../../../../ui-kit/kuika-tag/kuika-tag";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "../action-picker-popup/action-picker-popup.scss";
import {
  getPickerSurfaceStyle,
  PICKER_COLLISION_PADDING,
  PICKER_SIDE_OFFSET,
  usePickerPlacement,
  type IPickerPlacementHandle,
  type PickerPopupSide
} from "../picker-columns/picker-placement";
import {
  keepPickerFieldFocus,
  keepPickerFocusInField,
  useCloseOnIframeFocus,
  usePickerColumns,
  useRevealNewestColumn,
  type IPickerColumn,
  type IPickerColumnsController
} from "../picker-columns/use-picker-columns";
import { SymbolPickerOptionLabel } from "../symbol-picker-option-testids";
import {
  buildSymbolPickerSearchIndex,
  ESymbolPickerNodeKind,
  getSymbolPickerSelectedOptions,
  getSymbolPickerValue,
  isSymbolPickerBranch,
  isSymbolPickerPickable,
  searchSymbolPicker,
  toSymbolPickerNodes,
  type ISymbolPickerNode,
  type ISymbolPickerSearchEntry,
  type ISymbolPickerSearchResult
} from "./symbol-picker-node";
import { SymbolPickerPreview } from "./symbol-picker-preview";
import type { ISymbolPickerPreviewContext } from "./symbol-picker-preview-model";
import "./symbol-picker-popup.scss";

/** antd's Cascader placements, as the hosts already pass them. */
export type SymbolPickerPopupPlacement = "bottomLeft" | "bottomRight" | "topLeft" | "topRight";

/**
 * The prefix the search rows' keyword highlight is rendered with. antd's, on purpose: the rows are
 * rendered by the same `renderSymbolPickerSearchRow` the Cascader used, and the E2E suite reads the
 * highlight by `ant-cascader-menu-item-keyword`.
 */
const SEARCH_HIGHLIGHT_PREFIX: string = "ant-cascader";

export interface ISymbolPickerPopupProps {
  /** The DECORATED option tree (symbol-picker-option-testids.tsx) — the one the Cascader rendered. */
  options: CascaderOptionType[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives exactly what antd's Cascader passed its `onChange`: the options' values and the options, root → leaf. */
  onChange: (value: CascaderValueType, selectedOptions: CascaderOptionType[]) => void;
  /** The trigger. Must accept a ref (Radix anchors to it). */
  children: React.ReactElement;
  /** `<name>SymbolPicker` — the search field is `<testIdBase>PopupSearch`, as in the Cascader's header. */
  testIdBase: string;
  placeholder?: string;
  /** Extra class on the popup surface (the host's `kcascader-popup-<id>` handle). */
  className?: string;
  popupPlacement?: SymbolPickerPopupPlacement;
  /**
   * The host sits mid-screen without being a modal or a popover (the Document Parameters canvas), so it
   * gets the compact popup a dialog host gets on its own (see `usePickerPlacement`).
   */
  midScreen?: boolean;
  /**
   * The host's facts for the preview pane (the field being bound, its type, lookups into the
   * screen). Optional: without it the popup draws what it always drew.
   */
  previewContext?: ISymbolPickerPreviewContext;
}

interface IPopoverPlacement {
  position: PickerPopupSide;
  align: "start" | "end";
}

interface IPointerPosition {
  x: number;
  y: number;
}

/**
 * Right-anchored by default: the pickers sit in the right panel, so the popup hangs from the trigger's
 * right edge and grows left into the canvas, the way the action picker's "+" popup does. A host that
 * names a placement keeps it.
 */
function toPopoverPlacement(placement: SymbolPickerPopupPlacement | undefined): IPopoverPlacement {
  switch (placement) {
    case "bottomLeft":
      return { position: "bottom", align: "start" };
    case "topLeft":
      return { position: "top", align: "start" };
    case "topRight":
      return { position: "top", align: "end" };
    default:
      return { position: "bottom", align: "end" };
  }
}

type ISymbolColumn = IPickerColumn<ISymbolPickerNode>;

const NO_MATCHES: ISymbolPickerSearchEntry[] = [];

const getNodeChildren = (node: ISymbolPickerNode): ISymbolPickerNode[] => node.children;
const getNodeTitle = (node: ISymbolPickerNode): string => node.name;

/**
 * A browse row's label. A row the Cascader drew from a plain string carries the decorator's element —
 * `symbolPickerOptionPath-…` around `symbolPickerOption-<label>`, the ids the E2E suite walks — and is
 * rendered as is. A row whose builder drew its own label (an action result with its type caption and
 * tag) is rebuilt from the same facts as plain data, in the popup's row layout.
 */
function renderRowLabel(node: ISymbolPickerNode): React.ReactNode {
  const label: unknown = node.option.label;
  if (React.isValidElement(label) && label.type === SymbolPickerOptionLabel) {
    return <span className="kuika-action-picker-popup__row-label">{label}</span>;
  }
  return (
    <>
      <span className="kuika-action-picker-popup__row-label" title={node.name}>
        {node.name}
      </span>
      {node.actionTag && <KuikaTag type="action" label={node.actionTag} />}
      {node.typeCaption && <span className="kuika-symbol-picker-popup__type">{node.typeCaption}</span>}
    </>
  );
}

interface IRowProps {
  node: ISymbolPickerNode;
  isOpen: boolean;
  isCursor: boolean;
  onPointerEnter: (node: ISymbolPickerNode, event: React.MouseEvent) => void;
  onPointerMove: (node: ISymbolPickerNode, event: React.MouseEvent) => void;
  onPointerLeave: () => void;
  onActivate: (node: ISymbolPickerNode) => void;
}

const SymbolPickerRow = ({
  node,
  isOpen,
  isCursor,
  onPointerEnter,
  onPointerMove,
  onPointerLeave,
  onActivate
}: IRowProps): JSX.Element => {
  const isBranch: boolean = node.kind === ESymbolPickerNodeKind.branch;
  const isLoading: boolean = node.kind === ESymbolPickerNodeKind.loading;
  const isInert: boolean = node.isDisabled || isLoading;
  const row: JSX.Element = (
    <div
      role="option"
      aria-selected={isOpen || isCursor}
      aria-disabled={isInert || undefined}
      aria-busy={isLoading || undefined}
      data-cursor={isCursor || undefined}
      className={classNames("kuika-action-picker-popup__row", "kuika-symbol-picker-popup__row", {
        "kuika-action-picker-popup__row--open": isOpen,
        "kuika-action-picker-popup__row--cursor": isCursor,
        "kuika-action-picker-popup__row--dimmed": isInert
      })}
      onMouseDown={keepPickerFieldFocus}
      onMouseEnter={(event: React.MouseEvent): void => onPointerEnter(node, event)}
      onMouseMove={(event: React.MouseEvent): void => onPointerMove(node, event)}
      onMouseLeave={onPointerLeave}
      onClick={(): void => onActivate(node)}
    >
      {isBranch && (
        <KuikaSvg
          className="kuika-action-picker-popup__chevron"
          type="arrows"
          name="chevron-left"
          width={14}
          height={14}
        />
      )}
      {isLoading && <KuikaLoader size="sm" decorative />}
      {renderRowLabel(node)}
    </div>
  );
  if (node.isDisabled && node.disabledReason) {
    return (
      <KuikaTooltip content={node.disabledReason} position="left">
        {row}
      </KuikaTooltip>
    );
  }
  return row;
};

interface IResultRowProps {
  entry: ISymbolPickerSearchEntry;
  query: string;
  isCursor: boolean;
  onPointerRest: (node: ISymbolPickerNode) => void;
  onActivate: (node: ISymbolPickerNode) => void;
}

const SymbolPickerResultRow = ({ entry, query, isCursor, onPointerRest, onActivate }: IResultRowProps): JSX.Element => {
  const isInert: boolean = !isSymbolPickerPickable(entry.node);
  return (
    <div
      role="option"
      aria-selected={isCursor}
      aria-disabled={isInert || undefined}
      data-cursor={isCursor || undefined}
      className={classNames("kuika-action-picker-popup__row", "kuika-symbol-picker-popup__result", {
        "kuika-action-picker-popup__row--cursor": isCursor,
        "kuika-action-picker-popup__row--dimmed": isInert
      })}
      onMouseDown={keepPickerFieldFocus}
      onMouseEnter={(): void => onPointerRest(entry.node)}
      onClick={(): void => onActivate(entry.node)}
    >
      {renderSymbolPickerSearchRow(query, entry.path, SEARCH_HIGHLIGHT_PREFIX)}
    </div>
  );
};

/**
 * The symbol / value picker's popup on the shared column engine (picker-columns/use-picker-columns.ts)
 * — the successor of the antd Cascader popup, behind the `symbolPickerColumnsV2` flag.
 *
 * Same tree, same search (the picker's own matcher and ranking, capped at 300 as the Cascader capped
 * it), same test-ids on the rows. It only chooses: the host's `onChange` receives the `(value,
 * selectedOptions)` pair antd's Cascader delivered, so the symbol is built by the same
 * `buildSymbolFromSelectedOptions` and written by the same handlers.
 */
export const SymbolPickerPopup = ({
  options,
  open,
  onOpenChange,
  onChange,
  children,
  testIdBase,
  placeholder = "Search..",
  className,
  popupPlacement,
  midScreen,
  previewContext
}: ISymbolPickerPopupProps): JSX.Element => {
  const nodes: ISymbolPickerNode[] = useMemo((): ISymbolPickerNode[] => toSymbolPickerNodes(options), [options]);
  const nodesByKey: Map<string, ISymbolPickerNode> = useMemo((): Map<string, ISymbolPickerNode> => {
    const map: Map<string, ISymbolPickerNode> = new Map<string, ISymbolPickerNode>();
    const walk = (list: ISymbolPickerNode[]): void => {
      for (const node of list) {
        map.set(node.key, node);
        walk(node.children);
      }
    };
    walk(nodes);
    return map;
  }, [nodes]);

  const [query, setQuery] = useState<string>("");
  const searchRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null> = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);

  // rc-cascader's order: the host hears the pick, then the popup closes.
  const pick = useCallback(
    (node: ISymbolPickerNode): void => {
      if (!isSymbolPickerPickable(node)) {
        return;
      }
      const selectedOptions: CascaderOptionType[] = getSymbolPickerSelectedOptions(node);
      onChange(getSymbolPickerValue(selectedOptions), selectedOptions);
      onOpenChange(false);
    },
    [onChange, onOpenChange]
  );

  const engine: IPickerColumnsController<ISymbolPickerNode> = usePickerColumns<ISymbolPickerNode>({
    rootItems: nodes,
    rootTitle: "Bind to",
    getChildren: getNodeChildren,
    getTitle: getNodeTitle,
    isBranch: isSymbolPickerBranch,
    isPickable: isSymbolPickerPickable,
    onPick: pick
  });
  const {
    columns,
    openKeys,
    setOpenKeys,
    previewKey,
    setPreviewKey,
    cursor,
    setCursor,
    stripRef,
    handleRowRest,
    handleRowLeave,
    handleRowActivate,
    handleStripWheel
  } = engine;

  const searchIndex: ISymbolPickerSearchEntry[] = useMemo(
    (): ISymbolPickerSearchEntry[] => buildSymbolPickerSearchIndex(nodes),
    [nodes]
  );
  const isSearching: boolean = query.trim() !== "";
  const searchResult: ISymbolPickerSearchResult | null = useMemo(
    (): ISymbolPickerSearchResult | null => (isSearching ? searchSymbolPicker(searchIndex, query) : null),
    [isSearching, searchIndex, query]
  );
  const matches: ISymbolPickerSearchEntry[] = searchResult?.matches ?? NO_MATCHES;
  const matchNodes: ISymbolPickerNode[] = useMemo(
    (): ISymbolPickerNode[] => matches.map((entry: ISymbolPickerSearchEntry): ISymbolPickerNode => entry.node),
    [matches]
  );

  // Below the trigger unless there is too little room there (picker-columns/picker-placement.ts),
  // decided on open against the trigger's box.
  const requested: IPopoverPlacement = toPopoverPlacement(popupPlacement);
  const { placement, trigger, isMidScreen }: IPickerPlacementHandle = usePickerPlacement(
    open,
    requested.position,
    children
  );
  const isCompact: boolean = isMidScreen || midScreen === true;

  const previewNode: ISymbolPickerNode | null = previewKey ? (nodesByKey.get(previewKey) ?? null) : null;
  // The preview follows what the pointer or the keyboard cursor is ON — a category row included (it
  // describes the category) — and never a row nobody pointed at. With no cursor it keeps the last
  // previewed leaf, so the pane does not blank while the pointer crosses a gap.
  let cursorNode: ISymbolPickerNode | null = null;
  if (cursor) {
    cursorNode = isSearching ? (matchNodes[cursor.row] ?? null) : (columns[cursor.column]?.items[cursor.row] ?? null);
  }
  const pointedCandidate: ISymbolPickerNode | null = cursorNode ?? previewNode;
  const pointedNode: ISymbolPickerNode | null =
    pointedCandidate && pointedCandidate.kind !== ESymbolPickerNodeKind.loading ? pointedCandidate : null;
  const lastColumn: ISymbolColumn = columns[columns.length - 1];
  const hasLeafColumn: boolean = lastColumn.items.some(
    (node: ISymbolPickerNode): boolean => node.kind !== ESymbolPickerNodeKind.branch
  );
  // A mid-screen host (a modal, a popover, or `midScreen`) is narrow: without the pane more columns fit before Radix pushes the popup
  // off its trigger. A row still binds on click or Enter.
  const isPreviewShown: boolean =
    !isCompact && (isSearching ? matches.length > 0 : hasLeafColumn || pointedNode !== null);

  // Reset on the open transition only. The host swaps the tree under an open popup when the dynamic
  // types finish loading (the Action Results placeholder is spliced out); resetting on that would
  // collapse the columns mid-browse, and keys that no longer resolve drop out of `columns` anyway.
  useEffect((): void => {
    if (open) {
      setOpenKeys([]);
      setPreviewKey(null);
      setQuery("");
      setCursor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A row counts as pointed at only when the POINTER moved onto it. When a column opens, Radix may
  // shift the popup sideways to keep it on screen and slide the new column under a pointer that has
  // not moved; the browser then reports a mouseenter on whatever row slid there (the branch's first
  // leaf — "Now" under "Default"). Such an enter, at the very coordinates the pointer had when the
  // columns changed, is held until the pointer actually moves.
  const lastPointer: React.MutableRefObject<IPointerPosition | null> = useRef<IPointerPosition | null>(null);
  const frozenPointer: React.MutableRefObject<IPointerPosition | null> = useRef<IPointerPosition | null>(null);
  const heldRowKey: React.MutableRefObject<string | null> = useRef<string | null>(null);
  // Keyed on the columns themselves: swapping one open branch for another keeps their count.
  useEffect((): void => {
    frozenPointer.current = lastPointer.current;
    heldRowKey.current = null;
  }, [columns]);

  const handleRowEnter = useCallback(
    (node: ISymbolPickerNode, event: React.MouseEvent): void => {
      const frozen: IPointerPosition | null = frozenPointer.current;
      if (frozen && frozen.x === event.clientX && frozen.y === event.clientY) {
        heldRowKey.current = node.key;
        return;
      }
      heldRowKey.current = null;
      handleRowRest(node);
    },
    [handleRowRest]
  );

  const handleRowMove = useCallback(
    (node: ISymbolPickerNode, event: React.MouseEvent): void => {
      const frozen: IPointerPosition | null = frozenPointer.current;
      if (frozen && (frozen.x !== event.clientX || frozen.y !== event.clientY)) {
        frozenPointer.current = null;
      }
      if (heldRowKey.current === node.key && frozenPointer.current === null) {
        heldRowKey.current = null;
        handleRowRest(node);
      }
    },
    [handleRowRest]
  );

  const trackPointer = (event: React.MouseEvent): void => {
    lastPointer.current = { x: event.clientX, y: event.clientY };
  };

  useEffect((): void => {
    setCursor(isSearching ? { column: 0, row: 0 } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isSearching]);

  // The first hit is what Enter binds; keep the preview on it until the pointer moves.
  useEffect((): void => {
    if (isSearching) {
      setPreviewKey(matchNodes[0]?.key ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult]);

  useRevealNewestColumn(stripRef, openKeys, isPreviewShown);
  useCloseOnIframeFocus(open, onOpenChange);

  const handleResultRest = useCallback(
    (node: ISymbolPickerNode): void => engine.handleResultRest(node, matchNodes.indexOf(node)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchNodes]
  );

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (isSearching) {
      engine.handleResultsKeyDown(event, matchNodes);
      return;
    }
    engine.handleBrowseKeyDown(event);
  };

  // Every key stays in the popup — Escape included. Radix has already seen Escape by now (its dismiss
  // listener captures on the document), but the popup is portalled from inside the host's React tree,
  // and a picker in a KModal (the Add Condition dialog) would otherwise hand Escape on to rc-dialog,
  // closing the dialog along with the popup.
  const stopKeyPropagation = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    event.stopPropagation();
  };

  // The first Escape clears a query; the next one closes.
  const handleEscape = (event: KeyboardEvent): void => {
    if (query !== "") {
      event.preventDefault();
      setQuery("");
    }
  };

  const resultCountLabel: string | null = searchResult
    ? `${matches.length}${searchResult.truncated ? "+" : ""} results`
    : null;

  const renderBrowse = (): JSX.Element => (
    <>
      {columns.map((column: ISymbolColumn, columnIndex: number): JSX.Element => (
        <div
          key={column.owner?.key ?? "root"}
          className={classNames("kuika-action-picker-popup__column", {
            "kuika-action-picker-popup__column--root": columnIndex === 0,
            "kuika-action-picker-popup__column--actions": column.items.some(
              (node: ISymbolPickerNode): boolean => node.kind !== ESymbolPickerNodeKind.branch
            )
          })}
          role="listbox"
          aria-label={column.title}
          data-testid={`symbolPickerColumn-${columnIndex}`}
        >
          <div className="kuika-action-picker-popup__column-title" title={column.title}>
            {column.title}
          </div>
          <div className="kuika-action-picker-popup__column-rows">
            {column.items.map((node: ISymbolPickerNode, rowIndex: number): JSX.Element => (
              <SymbolPickerRow
                key={node.key}
                node={node}
                isOpen={openKeys[columnIndex] === node.key || previewKey === node.key}
                isCursor={cursor?.column === columnIndex && cursor.row === rowIndex}
                onPointerEnter={handleRowEnter}
                onPointerMove={handleRowMove}
                onPointerLeave={(): void => handleRowLeave(columnIndex)}
                onActivate={handleRowActivate}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );

  const renderSearch = (): JSX.Element => (
    <div
      className="kuika-action-picker-popup__column kuika-action-picker-popup__column--results kuika-symbol-picker-popup__results"
      role="listbox"
      aria-label="Search results"
      data-testid="symbolPickerSearchResults"
    >
      <div className="kuika-action-picker-popup__column-title">{resultCountLabel}</div>
      <div className="kuika-action-picker-popup__column-rows">
        {matches.map((entry: ISymbolPickerSearchEntry, rowIndex: number): JSX.Element => (
          <SymbolPickerResultRow
            key={entry.node.key}
            entry={entry}
            query={query}
            isCursor={cursor?.row === rowIndex}
            onPointerRest={handleResultRest}
            onActivate={pick}
          />
        ))}
        {searchResult?.truncated && (
          <div className="kuika-action-picker-popup__more">
            Showing the first {SEARCH_MATCH_LIMIT}. Add another word to narrow the list.
          </div>
        )}
      </div>
    </div>
  );

  const renderEmpty = (svgName: string, title: string, description: string, footer?: React.ReactNode): JSX.Element => (
    <div className="kuika-action-picker-popup__empty" data-testid="symbolPickerEmpty">
      <KuikaEmptyState
        variant="embedded"
        svgName={svgName}
        customName={title}
        description={description}
        footer={footer}
      />
    </div>
  );

  const renderBody = (): JSX.Element => {
    if (nodes.length === 0) {
      return renderEmpty(
        "search-off",
        "Nothing to bind here",
        "No value in this context matches the type this field takes."
      );
    }
    if (isSearching && matches.length === 0) {
      return renderEmpty(
        "search-off",
        `No values match "${query.trim()}"`,
        "Check the spelling, or search for where the value comes from, such as a component or an action.",
        <KuikaButton type="secondary" size="sm" onMouseDown={keepPickerFieldFocus} onClick={(): void => setQuery("")}>
          Clear search
        </KuikaButton>
      );
    }
    return (
      <div
        ref={stripRef}
        className="kuika-action-picker-popup__strip"
        onWheel={handleStripWheel}
        onMouseMove={trackPointer}
      >
        {isSearching ? renderSearch() : renderBrowse()}
        {isPreviewShown && <SymbolPickerPreview node={pointedNode} onSelect={pick} context={previewContext} />}
      </div>
    );
  };

  const breadcrumb: ISymbolPickerNode[] = columns
    .map((column: ISymbolColumn): ISymbolPickerNode | null => column.owner)
    .filter((owner: ISymbolPickerNode | null): owner is ISymbolPickerNode => owner !== null);

  const content: JSX.Element = (
    <div
      className="kuika-action-picker-popup__surface"
      data-testid="symbolPickerPopup"
      // The room below the trigger, so the popup fits there and Radix never flips it upward.
      style={getPickerSurfaceStyle(placement)}
    >
      <div className="kuika-action-picker-popup__header">
        <div className="kuika-action-picker-popup__search-row">
          <KuikaInput
            ref={searchRef}
            className="kuika-action-picker-popup__search"
            size="md"
            value={query}
            placeholder={placeholder}
            data-testid={`${testIdBase}PopupSearch`}
            aria-label={placeholder}
            leftIcon={<KuikaSvg name="search" width={16} height={16} />}
            rightUnit={
              resultCountLabel ? (
                <span
                  className="kuika-action-picker-popup__result-count"
                  data-testid={`${testIdBase}PopupSearchResultCount`}
                >
                  {resultCountLabel}
                </span>
              ) : undefined
            }
            allowClear
            onChange={(_event: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
              setQuery(String(value))
            }
            onKeyDown={handleSearchKeyDown}
          />
        </div>
      </div>
      {renderBody()}
      <div className="kuika-action-picker-popup__footer">
        <span className="kuika-action-picker-popup__crumbs">
          {breadcrumb.length > 0 && !isSearching
            ? breadcrumb.map((owner: ISymbolPickerNode, index: number): JSX.Element => (
                <React.Fragment key={owner.key}>
                  {index > 0 && <span className="kuika-action-picker-popup__crumb-separator">›</span>}
                  <span>{owner.name}</span>
                </React.Fragment>
              ))
            : null}
        </span>
        <span className="kuika-action-picker-popup__keys">
          <span className="kuika-action-picker-popup__kbd">↑↓</span> move
          {!isSearching && (
            <>
              <span className="kuika-action-picker-popup__kbd">←</span> open
              <span className="kuika-action-picker-popup__kbd">→</span> back
            </>
          )}
          <span className="kuika-action-picker-popup__kbd">↵</span> bind
        </span>
      </div>
    </div>
  );

  return (
    <KuikaPopover
      visible={open}
      onVisibleChange={onOpenChange}
      content={content}
      position={placement.side}
      align={requested.align}
      sideOffset={PICKER_SIDE_OFFSET}
      collisionPadding={PICKER_COLLISION_PADDING}
      className={classNames(
        "kuika-action-picker-popup",
        "kuika-symbol-picker-popup",
        { "kuika-symbol-picker-popup--compact": isCompact },
        className
      )}
      onEscapeKeyDown={handleEscape}
      onKeyDown={stopKeyPropagation}
      onMouseDownCapture={keepPickerFocusInField}
      onOpenAutoFocus={(event: Event): void => {
        event.preventDefault();
        searchRef.current?.focus();
      }}
    >
      {trigger}
    </KuikaPopover>
  );
};

export default SymbolPickerPopup;
